import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import * as S from './AiConversationBanner.styles';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChat } from '@/hooks/AiChat';
import { AiChatForm, MiniCard, Header } from './components';
import { commonActions, homeActions, merlinActions } from '@/redux/modules';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  ATIVACAO_AICHAT_AUDIO_RECORDING, ATIVACAO_ENABLE_AICHAT_GUIDE, ATIVACAO_N8N_INHOUSE_URLS,
  OPERACOES_ENABLE_SUPPORT_TICKETS_CTA,
} from '@/config/GrowthBook/constants';
import { WSS_ROUTES } from '@/components/Layout/Menu/HeaderV2/AiChat/AiChat.enum';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useResize } from '@/hooks/useResize';
import { IconInfo } from 'gatorcomponents';
import { useChatbot } from '@/contexts/Chatbot';
import { AiAlertsCta } from '@/components/Layout/Menu/HeaderV2/AiChat/components/AiAlertsCta';
import { SupportTicketsCta } from '@/components/Layout/Menu/HeaderV2/AiChat/components/SupportTicketsCta';
import { OPTIONS } from '@/components/Layout/Menu/HeaderV2/components/NewAiHeaderButton/NewAiHeaderButton.types';
import { gatorAnalytics } from '@/analytics/gator';
import useLocale from '@/hooks/useLocale';
import { loadPendingTicketsV2 } from '@/redux/actions/tickets';

const AiConversationBanner = ({ locale, aichatLocale }) => {
  const {
    betaTag, title, suggestions, input,
  } = locale;

  const { alertsCta: alertsCtaLocale, supportTicketsCta: supportTicketsCtaLocale } = aichatLocale;

  const history = useHistory();
  const { routes: routesLocale } = useLocale();
  const dispatch = useDispatch();
  const chatbot = useChatbot();
  const { triggerExternalMessage, triggerExternalAudioBlob } = useAiChat();
  const handleAudioStop = useCallback((blob) => {
    gatorAnalytics.gatorHomeSendMessage({ messageType: 'VOICE' });
    triggerExternalAudioBlob(blob);
  }, [triggerExternalAudioBlob]);
  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({ onStop: handleAudioStop });
  const { firstname } = useSelector(state => state.summary);
  const {
    currentChat, loadingTranscription, alerts, alertsTypes,
  } = useSelector(state => state.merlin);
  const openTickets = useSelector(state => state.tickets.pending.ticketsV2);

  const [messageContent, setMessageContent] = useState('');
  const [renderedSuggestions, setRenderedSuggestions] = useState([]);
  const [interactedWithSuggestions, setInteractedWithSuggestions] = useState(false);
  const [storedRef, setStoredRef] = useState(null);
  const aiWrapperRef = useRef();
  const windowRect = useResize();
  const isMobile = windowRect && windowRect.width <= 960;

  const isAudioRecordingFeatureActive = useFeatureIsOn(ATIVACAO_AICHAT_AUDIO_RECORDING);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);
  const enabledAiGuide = useFeatureIsOn(ATIVACAO_ENABLE_AICHAT_GUIDE);
  const enableSupportTicketsCta = useFeatureIsOn(OPERACOES_ENABLE_SUPPORT_TICKETS_CTA);

  const [loadingTickets] = useState(false);

  const chatIsOpen = Boolean(currentChat && currentChat.threadid);
  const hasOpenTickets = openTickets.length > 0;
  const showSupportTicketsCta = enableSupportTicketsCta && (loadingTickets || hasOpenTickets);
  const isChatbotLoading = chatbot.isActive && !chatbot.isReady;
  const isRecording = recording || loadingTranscription;
  const isFullScreen = currentChat && currentChat.fullscreen;

  const getOpenTickets = useCallback(() => {
    dispatch(loadPendingTicketsV2());
  }, [dispatch]);

  useEffect(() => {
    if (!enableSupportTicketsCta) {
      return;
    }
    getOpenTickets();
  }, [enableSupportTicketsCta, getOpenTickets]);

  useEffect(() => {
    if (chatbot.isActive && openTickets && openTickets.length > 0) {
      chatbot.updateSupportTickets({ tickets: openTickets });
    }
  }, [chatbot, openTickets]);

  const handleSupportTicketsCtaClick = useCallback(() => {
    history.push(routesLocale.help);
  }, [history, routesLocale]);

  const updateAiWrapperRef = useCallback(() => {
    if (aiWrapperRef && aiWrapperRef.current && (!storedRef || storedRef.offsetHeight !== aiWrapperRef.current.offsetHeight)) {
      const aiWrapperRefData = {
        offsetLeft: aiWrapperRef.current.offsetLeft,
        offsetTop: aiWrapperRef.current.offsetTop,
        offsetWidth: aiWrapperRef.current.offsetWidth,
        offsetHeight: aiWrapperRef.current.offsetHeight,
      };

      setStoredRef(aiWrapperRefData);
      dispatch(homeActions.setHomeAiWrapperRef({ aiWrapperRef: aiWrapperRefData }));
    }
  }, [dispatch, storedRef]);

  useEffect(() => {
    updateAiWrapperRef();
  }, [updateAiWrapperRef]);

  useEffect(() => {
    if (isMobile) {
      setRenderedSuggestions(suggestions.filter(suggestion => suggestion.action !== 'expand' && !suggestion.hidden));
      return;
    }
    setRenderedSuggestions(suggestions.filter(suggestion => !suggestion.hideOnInit && !suggestion.hidden));
  }, [suggestions, isMobile]);

  const handleExpandSuggestions = () => {
    setRenderedSuggestions(suggestions.filter(suggestion => suggestion.action !== 'expand' && !suggestion.hidden));
  };

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();

    if (chatIsOpen || !messageContent) {
      return;
    }

    gatorAnalytics.gatorHomeSendMessage({ messageType: 'TYPED' });
    triggerExternalMessage({ message: messageContent.trim() });
    setMessageContent('');
  }, [chatIsOpen, messageContent, triggerExternalMessage]);

  const handleEnter = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleFormSubmit(event);
    }
  }, [handleFormSubmit]);

  const dispatchGaEvent = ({ value }) => {
    if (value === OPTIONS.MORE) return;
    gatorAnalytics.gatorShortcut({ label: value });
  };

  const handleSelectSuggestion = ({
    action, initialMessage, value = '',
  }) => {
    if (action === 'expand') {
      handleExpandSuggestions();
      return;
    }

    dispatchGaEvent({ value });

    const setRoute = (route) => {
      dispatch(merlinActions.inhouse.setRoute(route));
    };

    const routes = {
      email: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
      domainConfig: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
      newSite: wssToggleRoutes.site || WSS_ROUTES.SITE,
      general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
    };

    setRoute(routes[action] || routes.general);
    if (!chatbot.isActive) {
      const routes = {
        email: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
        domainConfig: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
        newSite: wssToggleRoutes.site || WSS_ROUTES.SITE,
        general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
      };
      dispatch(merlinActions.inhouse.setRoute(routes[action] || routes.general));
    } else {
      const routes = {
        email: 'email',
        domainConfig: 'domain',
        newSite: 'site',
        general: 'general',
      };
      chatbot.setRoute(routes[action] || routes.general);
    }
    triggerExternalMessage({ message: initialMessage });
  };

  const handleScrollSuggestions = () => {
    setInteractedWithSuggestions(true);
  };

  const handleShowGuide = () => {
    dispatch(commonActions.setShouldRenderAiGuide(true));
  };

  const renderSuggestions = ({ noWrap = false } = {}) => renderedSuggestions.map(suggestion => (
    <MiniCard
      key={suggestion.id}
      Icon={suggestion.Icon}
      label={suggestion.label}
      onClick={() => handleSelectSuggestion({
        action: suggestion.action,
        initialMessage: suggestion.initialMessage,
        value: suggestion.value,
      })}
      disabled={chatIsOpen || isChatbotLoading}
      noWrap={noWrap}
    />
  ));

  const getAiChatFormProps = useCallback(() => ({
    placeholder: chatIsOpen ? input.placeholderAlreadyOpened : input.placeholder,
    value: messageContent,
    onChange: e => setMessageContent(e.target.value),
    onKeyDown: handleEnter,
    isRecording,
    isAudioRecordingFeatureActive,
    handleStartRecording,
    onSubmit: handleFormSubmit,
    chatIsOpen,
    handleStopRecording,
    recording,
    loadingTranscription,
    audioLocale: locale.audio,
    disabled: chatIsOpen || isRecording || isChatbotLoading,
  }), [
    chatIsOpen,
    messageContent,
    isRecording,
    isAudioRecordingFeatureActive,
    handleStartRecording,
    handleFormSubmit,
    handleStopRecording,
    recording,
    loadingTranscription,
    locale,
    handleEnter,
    input,
    isChatbotLoading,
  ]);

  const getSupportTicketsCtaProps = useCallback(() => ({
    label: supportTicketsCtaLocale.label(openTickets.length),
    cta: supportTicketsCtaLocale.cta,
    loading: loadingTickets,
    onClick: handleSupportTicketsCtaClick,
  }), [supportTicketsCtaLocale, openTickets, loadingTickets, handleSupportTicketsCtaClick]);

  const getAiAlertsCtaProps = useCallback(() => {
    if (!alertsTypes) {
      return;
    }

    const alertsArrays = alertsTypes && Object.values(alertsTypes);
    const hasLoaded = alertsArrays.some(alert => alert.loaded);

    return ({
      label: alertsCtaLocale && alertsCtaLocale.label,
      cta: alertsCtaLocale.cta,
      alerts,
      loading: !hasLoaded,
      alertsTypes,
    });
  }, [alertsCtaLocale, alertsTypes, alerts]);

  const alertsCtaProps = getAiAlertsCtaProps();

  const ctaSlot = (showSupportTicketsCta && <SupportTicketsCta {...getSupportTicketsCtaProps()} />)
    || (alertsCtaProps && <AiAlertsCta {...alertsCtaProps} />)
    || null;

  return (
    <S.Wrapper data-testid="ai-conversation-banner" ref={aiWrapperRef}>
      <Header betaTag={betaTag} title={title} firstname={firstname} isRecording={isRecording} />
      <S.ConversationBox>
        {ctaSlot}
        <S.AiChatFormAdapter>
          <AiChatForm {...getAiChatFormProps()} />
        </S.AiChatFormAdapter>
        <S.SuggestionsWrapper data-testid="suggestions-wrapper" data-interacted={interactedWithSuggestions} $interacted={interactedWithSuggestions} onScroll={handleScrollSuggestions}>
          {renderSuggestions({ noWrap: true })}
        </S.SuggestionsWrapper>
      </S.ConversationBox>
      {(enabledAiGuide && !isFullScreen) && (
        <S.GuideButton onClick={() => handleShowGuide()}>
          <IconInfo color="highPure" />
        </S.GuideButton>
      )}
    </S.Wrapper>
  );
};

export default AiConversationBanner;
